import { Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { concat } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { ART_DES_ANTRAGS, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import { InhaltTrenner } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getProjektLaufzeitBIS_Validation,
	getProjektLaufzeitVON_Validation,
} from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { formStrucProjektdatenPruefassistentGlobal } from 'src/utils/formStruc';
import { checkReadOnlyInUeberarbeitung } from 'src/utils/helper';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';

// import schemas
import { get } from 'lodash';
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const MAX_CHAR_LENGTH = 6000;

const ProjektDatenAntragLoewe: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);
	const isFormantrag = get(application, P.Allgemein_Art_Des_Antrags.path) === ART_DES_ANTRAGS.Formantrag;

	return (
		<>
			<ApplicationHeading title='Projektdaten' description={PLATZHALTER_TEXT} />
			<PruefAssistenz
				statusPath={P.Allgemein_Pruefung_Pruefstatus.path}
				hinweisPath={P.Allgemein_Pruefung_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Allgemein_Pruefung_Ueberarbeitet_Am.path}
				erledigtPath={P.Allgemein_Pruefung_Erledigt.path}
				formStruc={formStrucProjektdatenPruefassistentGlobal(application)}
			/>

			<Grid2 spacing={6} container>
				<Grid2 sm={6}>
					<CreateForm
						formStruc={formStrucSpalte1()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
					/>
				</Grid2>
				<Grid2 sm={6}>
					<CreateForm
						formStruc={formStrucSpalte2(application)}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
					/>
				</Grid2>
			</Grid2>

			<InhaltTrenner />
			<Typography variant='body1' sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
				{isFormantrag ? 'Kurze Zusammenfassung zum Projektstart' : 'Kurze Zusammenfassung des Projektes'}
			</Typography>
			{isFormantrag ?
				<>
					<Typography sx={{ mb: 0 }}>
						Bitte beschreiben Sie Ihr Vorhaben im Stil einer Pressemeldung kurz, prägnant und allgemein verständlich, wobei Sie auf nachstehende
						Fragen eingehen. Bitte vermeiden Sie dabei Fachbegriffe und Abkürzungen.
						<ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
							<li>Was wird gemacht? (Projektinhalt, Projektziel)</li>
							<li>
								Welcher Nutzen kann daraus erzielt werden? (Markt- und Kundennutzen, insbesondere Bedeutung des Projekts für die
								Öffentlichkeit)
							</li>
						</ul>
					</Typography>
				</>
			:	<Typography sx={{ mb: 0 }}>
					u.a. Angaben zum angestrebten Projektziel, Problemlösung, Herangehensweise; angestrebte Alleinstellungsmerkmale des Produkts oder
					Verfahrens; Kundennutzen, Marktpotenzial und Verwertung; wirtschaftliches bzw. wissenschaftliches Potential der erwarteten Ergebnisse,
					Standorteffekte …
				</Typography>
			}
			<CreateForm
				formStruc={formStrucZusammenfassung(application)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			{isFormantrag && (
				<>
					{' '}
					<InhaltTrenner />
					<Typography variant='body1' sx={{ fontWeight: 600, mt: 2 }}>
						Ort und Durchführung des Vorhabens
					</Typography>
					<Typography variant='body1' sx={{ fontWeight: 400, mb: 1 }}>
						(sofern abweichend von den Adressen der Antragstellenden bzw. der Verbundpartner)
					</Typography>
					<Grid2 container spacing={6}>
						<Grid2 sm={6}>
							<CreateForm
								formStruc={formStrucAbweichendeAdresse()}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
							/>
						</Grid2>
					</Grid2>
				</>
			)}
		</>
	);
};

export default ProjektDatenAntragLoewe;

const formStrucSpalte1 = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [P.Allgemein_Projekttitel],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Allgemein_Kurzwort, maxCharLength: 12 }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucSpalte2 = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Allgemein_Geplante_Projektlaufzeit_von,
					...getProjektLaufzeitVON_Validation(application),
				},
				{
					...P.Allgemein_Geplante_Projektlaufzeit_bis,
					...getProjektLaufzeitBIS_Validation(application),
				},
			],
		},
	];
	return struc;
};

const formStrucZusammenfassung = (application: any) => {
	const isFormantrag = get(application, P.Allgemein_Art_Des_Antrags.path) === ART_DES_ANTRAGS.Formantrag;
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Kurzen_Zusammenfassung_Des_Projektes,
					maxCharLength: isFormantrag ? 1300 : MAX_CHAR_LENGTH,
					hideLabel: true,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];

	return struc;
};

const formStrucAbweichendeAdresse = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [P.Antragstellende_Organisa_0234235106_resse_Strasse_Und_Hausnummer],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [P.Antragstellende_Organisation_Abweichender_Sitz_Adresse_PLZ, P.Antragstellende_Organisation_Abweichender_Sitz_Adresse_Ort],
			userProps: { columnToDivide: 2 },
		},
	];

	return struc;
};

export const getFormStrucProjektDatenAntragLoewe = (application: any) => {
	let result: FormStrucItem[] = [];
	result = concat(formStrucSpalte1(), formStrucSpalte2(application), formStrucZusammenfassung(application));

	if (checkIfPruefAssitenzNeedsToValidate(application, P.Allgemein_Pruefung_Pruefstatus.path, P.Allgemein_Pruefung_Erledigt.path)) {
		result = concat(formStrucProjektdatenPruefassistentGlobal(application), result);
	}
	return result;
};
